import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faFilter,
  faFileExport,
  faEye,
  faCheck,
  faRotateLeft,
  faClockRotateLeft,
  faSpinner,
  faSort,
  faSortUp,
  faSortDown,
  faXmark,
  faClipboardList,
  faGripLinesVertical,
} from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  INSPECTION_STATUS_LABELS,
  OPS_REVIEW_STATUS_LABELS,
  CUSTOMER_APPROVAL_LABELS,
  WORK_QUEUE_STAGE_LABELS,
  type Inspection,
  type InspectionStatus,
  type OperationsReviewStatus,
  type CustomerApprovalStatus,
} from '@/services/inspection.service';
// D-131 (G-1b, readiness §14.3) — filter the queue by the state of a job's OPEN
// modification round, so Operations finds "awaiting my feasibility review"
// without opening jobs one by one.
import type { ModificationRoundState } from '@/services/inspection-modification.service';
import {
  OPEN_ROUND_STATES,
  ROUND_STATE_LABELS,
} from '@/features/inspection/modification-state-rules';
import { dashboardService } from '@/services/dashboard.service';
import { customerService } from '@/services/customer.service';
import { priorityService } from '@/services/priority.service';
import type { OpenModificationRoundSummary } from '@/services/inspection.service';
import { usersService } from '@/services/users.service';
import { departmentService } from '@/services/department.service';
import type { EquipmentSearchResult } from '@/services/equipment.service';
import { EquipmentSearchCombobox } from '@/features/equipment-intake/EquipmentSearchCombobox';
import { KpiCard, KpiCardSkeleton } from '@/features/dashboard/components/KpiCard';
import {
  ReturnToEngineerDialog,
  type ReturnToEngineerTarget,
} from './components/ReturnToEngineerDialog';
import { useResizableColumns } from './use-resizable-columns';
import { STAGE_BADGE } from './stage-badge';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { SearchBar } from '@/components/shared/SearchBar';
import { Pagination } from '@/components/shared/Pagination';
import { EmptyState } from '@/components/shared/EmptyState';
import { SkeletonPage } from '@/components/skeletons';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';
import { useAuthStore } from '@/stores/auth.store';
import { toast, swalConfirm } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { formatCustomerLabel } from '@/lib/customer-label';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;
const STORAGE_KEY = 'ops-work-queue-filters';

const SELECT_CLASS =
  'flex h-10 rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';
const DATE_CLASS = SELECT_CLASS;

type SortField =
  | 'inspectionNumber'
  | 'receivedAt'
  | 'customerName'
  | 'assignedEngineerName'
  | 'priority'
  | 'status'
  | 'updatedAt';

interface Filters {
  jobNumber: string;
  customerId: string;
  equipmentId: string;
  equipmentLabel: string;
  engineerId: string;
  priorityId: string;
  status: InspectionStatus | '';
  operationsReviewStatus: OperationsReviewStatus | '';
  customerApprovalStatus: CustomerApprovalStatus | '';
  /** D-131 — state of the job's OPEN modification round ('' = no filter). */
  modificationRoundState: ModificationRoundState | '';
  departmentId: string;
  receivedFrom: string;
  receivedTo: string;
}

const DEFAULT_FILTERS: Filters = {
  jobNumber: '',
  customerId: '',
  equipmentId: '',
  equipmentLabel: '',
  engineerId: '',
  priorityId: '',
  status: '',
  operationsReviewStatus: '',
  customerApprovalStatus: '',
  modificationRoundState: '',
  departmentId: '',
  receivedFrom: '',
  receivedTo: '',
};

interface PersistedState {
  search: string;
  filters: Filters;
  sortBy?: SortField;
  sortDir: 'asc' | 'desc';
}

function loadPersisted(): Partial<PersistedState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<PersistedState>) : {};
  } catch {
    return {};
  }
}

interface ColumnDef {
  key: string;
  label: string;
  sortKey?: SortField;
  defaultWidth: number;
  hideBelow?: 'sm' | 'md' | 'lg';
}

const COLUMNS: ColumnDef[] = [
  { key: 'jobNumber', label: 'Job Number', sortKey: 'inspectionNumber', defaultWidth: 130 },
  {
    key: 'receivedDate',
    label: 'Received Date',
    sortKey: 'receivedAt',
    defaultWidth: 130,
    hideBelow: 'lg',
  },
  { key: 'customer', label: 'Customer', sortKey: 'customerName', defaultWidth: 160 },
  { key: 'equipment', label: 'Equipment', defaultWidth: 160, hideBelow: 'sm' },
  { key: 'serialNumber', label: 'Serial Number', defaultWidth: 140, hideBelow: 'lg' },
  {
    key: 'engineer',
    label: 'Engineer',
    sortKey: 'assignedEngineerName',
    defaultWidth: 150,
    hideBelow: 'md',
  },
  { key: 'priority', label: 'Priority', sortKey: 'priority', defaultWidth: 110, hideBelow: 'md' },
  { key: 'currentStage', label: 'Current Stage', defaultWidth: 150 },
  {
    key: 'lastUpdated',
    label: 'Last Updated',
    sortKey: 'updatedAt',
    defaultWidth: 130,
    hideBelow: 'lg',
  },
  { key: 'daysWaiting', label: 'Days Waiting', defaultWidth: 110, hideBelow: 'lg' },
  { key: 'actions', label: 'Actions', defaultWidth: 170 },
];
const DEFAULT_WIDTHS = Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultWidth]));

const HIDE_CLASS: Record<NonNullable<ColumnDef['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

export function OperationsWorkQueuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const roleSlug = useAuthStore((s) => s.user?.roleSlug);
  const [searchParams, setSearchParams] = useSearchParams();
  const persisted = useMemo(() => loadPersisted(), []);

  // OPS gets the new Review Workspace; every other role (Admin, Owner, etc.
  // reachable on this page via the same inspection:operations-review gate)
  // keeps the existing generic Inspection Detail page unchanged.
  const openTarget = (id: string) =>
    roleSlug === 'operations-user'
      ? ROUTES.OPS_REVIEW_WORKSPACE.replace(':inspectionId', id)
      : ROUTES.INSPECTION_DETAIL.replace(':id', id);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(persisted.search ?? '');
  const [filters, setFilters] = useState<Filters>({
    ...DEFAULT_FILTERS,
    ...persisted.filters,
    status:
      (searchParams.get('status') as InspectionStatus | null) ?? persisted.filters?.status ?? '',
    operationsReviewStatus:
      (searchParams.get('operationsReviewStatus') as OperationsReviewStatus | null) ??
      persisted.filters?.operationsReviewStatus ??
      '',
    customerApprovalStatus:
      (searchParams.get('customerApprovalStatus') as CustomerApprovalStatus | null) ??
      persisted.filters?.customerApprovalStatus ??
      '',
    // Request Thread Stage B — the one filter that was not URL-seedable, which
    // is what stopped anything linking to a pre-filtered queue. Same precedence
    // as the three above: URL wins, then the persisted value, then unfiltered.
    // The GLOBAL default stays unfiltered (OD-B2) — Operations also works
    // intake, assignment and report review from this queue, and defaulting it
    // to one state would bury all of that.
    modificationRoundState:
      (searchParams.get('modificationRoundState') as ModificationRoundState | null) ??
      persisted.filters?.modificationRoundState ??
      '',
  });
  const [sort, setSort] = useState<{ by: SortField | undefined; dir: 'asc' | 'desc' }>({
    by: persisted.sortBy,
    dir: persisted.sortDir ?? 'asc',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [returnTarget, setReturnTarget] = useState<ReturnToEngineerTarget | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);
  const debouncedJobNumber = useDebounce(filters.jobNumber, 300);
  const { widths, startResize } = useResizableColumns('ops-work-queue-columns', DEFAULT_WIDTHS);

  // Remember filter state — persisted to localStorage and mirrored into the URL.
  useEffect(() => {
    const state: PersistedState = { search, filters, sortBy: sort.by, sortDir: sort.dir };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore quota errors
    }
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.operationsReviewStatus)
      params.set('operationsReviewStatus', filters.operationsReviewStatus);
    if (filters.customerApprovalStatus)
      params.set('customerApprovalStatus', filters.customerApprovalStatus);
    // Stage B — mirrored back so the pre-filtered view stays shareable and
    // survives a reload, matching the three filters above.
    if (filters.modificationRoundState)
      params.set('modificationRoundState', filters.modificationRoundState);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters, sort]);

  const statsQuery = useQuery({
    queryKey: ['dashboard-operations'],
    queryFn: dashboardService.getOperationsDashboard,
    staleTime: 60_000,
  });

  const customersQuery = useQuery({
    queryKey: ['ops-work-queue-customers'],
    queryFn: () => customerService.list({ pageSize: 100 }),
    staleTime: 60_000,
  });
  const prioritiesQuery = useQuery({
    queryKey: ['ops-work-queue-priorities'],
    queryFn: priorityService.listAll,
    staleTime: 60_000,
  });
  const engineersQuery = useQuery({
    queryKey: ['ops-work-queue-engineers'],
    queryFn: usersService.listAll,
    staleTime: 60_000,
  });
  const departmentsQuery = useQuery({
    queryKey: ['ops-work-queue-departments'],
    queryFn: departmentService.listAll,
    staleTime: 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['ops-work-queue', page, debouncedSearch, debouncedJobNumber, filters, sort],
    queryFn: () =>
      inspectionService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        inspectionNumber: debouncedJobNumber || undefined,
        customerId: filters.customerId || undefined,
        equipmentId: filters.equipmentId || undefined,
        assignedEngineerId: filters.engineerId || undefined,
        priorityId: filters.priorityId || undefined,
        status: filters.status || undefined,
        operationsReviewStatus: filters.operationsReviewStatus || undefined,
        customerApprovalStatus: filters.customerApprovalStatus || undefined,
        modificationRoundState: filters.modificationRoundState || undefined,
        departmentId: filters.departmentId || undefined,
        receivedFrom: filters.receivedFrom || undefined,
        receivedTo: filters.receivedTo || undefined,
        sortBy: sort.by,
        sortDir: sort.by ? sort.dir : undefined,
      }),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['ops-work-queue'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-operations'] });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => inspectionService.operationsApprove(id),
    onSuccess: () => {
      toast.success('Approved for commercial');
      setApprovingId(null);
      invalidateAll();
    },
    onError: (e: Error) => {
      toast.error(getApiErrorMessage(e, 'Failed to approve'));
      setApprovingId(null);
    },
  });

  const handleApprove = async (item: Inspection) => {
    const ok = await swalConfirm({
      title: 'Approve for Commercial?',
      text: `This will mark job ${item.inspectionNumber} as approved and move it to commercial review.`,
      confirmText: 'Approve',
    });
    if (ok) {
      setApprovingId(item.id);
      approveMutation.mutate(item.id);
    }
  };

  const toggleSort = (key: SortField) => {
    setSort((prev) => {
      if (prev.by !== key) return { by: key, dir: 'asc' };
      if (prev.dir === 'asc') return { by: key, dir: 'desc' };
      return { by: undefined, dir: 'asc' };
    });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const activeChips: { key: string; label: string; onClear: () => void }[] = [];
  if (filters.jobNumber) {
    activeChips.push({
      key: 'jobNumber',
      label: `Job #: ${filters.jobNumber}`,
      onClear: () => setFilter('jobNumber', ''),
    });
  }
  if (filters.customerId) {
    const name = customersQuery.data?.items.find((c) => c.id === filters.customerId)?.name;
    activeChips.push({
      key: 'customer',
      label: `Customer: ${name ?? '…'}`,
      onClear: () => setFilter('customerId', ''),
    });
  }
  if (filters.equipmentId) {
    activeChips.push({
      key: 'equipment',
      label: `Equipment: ${filters.equipmentLabel || '…'}`,
      onClear: () => setFilters((prev) => ({ ...prev, equipmentId: '', equipmentLabel: '' })),
    });
  }
  if (filters.engineerId) {
    const eng = engineersQuery.data?.find((u) => u.id === filters.engineerId);
    activeChips.push({
      key: 'engineer',
      label: `Engineer: ${eng ? `${eng.firstName} ${eng.lastName}` : '…'}`,
      onClear: () => setFilter('engineerId', ''),
    });
  }
  if (filters.priorityId) {
    const p = prioritiesQuery.data?.find((p) => p.id === filters.priorityId);
    activeChips.push({
      key: 'priority',
      label: `Priority: ${p?.name ?? '…'}`,
      onClear: () => setFilter('priorityId', ''),
    });
  }
  if (filters.operationsReviewStatus) {
    activeChips.push({
      key: 'operationsReviewStatus',
      label: `OPS Review: ${OPS_REVIEW_STATUS_LABELS[filters.operationsReviewStatus]}`,
      onClear: () => setFilter('operationsReviewStatus', ''),
    });
  }
  if (filters.customerApprovalStatus) {
    activeChips.push({
      key: 'customerApprovalStatus',
      label: `Customer Decision: ${CUSTOMER_APPROVAL_LABELS[filters.customerApprovalStatus]}`,
      onClear: () => setFilter('customerApprovalStatus', ''),
    });
  }
  if (filters.modificationRoundState) {
    activeChips.push({
      key: 'modificationRoundState',
      label: `Modification Request: ${ROUND_STATE_LABELS[filters.modificationRoundState]}`,
      onClear: () => setFilter('modificationRoundState', ''),
    });
  }
  if (filters.status) {
    activeChips.push({
      key: 'status',
      label: `Status: ${INSPECTION_STATUS_LABELS[filters.status]}`,
      onClear: () => setFilter('status', ''),
    });
  }
  if (filters.departmentId) {
    const d = departmentsQuery.data?.find((d) => d.id === filters.departmentId);
    activeChips.push({
      key: 'department',
      label: `Dept: ${d?.name ?? '…'}`,
      onClear: () => setFilter('departmentId', ''),
    });
  }
  if (filters.receivedFrom || filters.receivedTo) {
    activeChips.push({
      key: 'dateRange',
      label: `Received: ${filters.receivedFrom || '…'} – ${filters.receivedTo || '…'}`,
      onClear: () => setFilters((prev) => ({ ...prev, receivedFrom: '', receivedTo: '' })),
    });
  }

  if (statsQuery.isLoading && listQuery.isLoading) return <SkeletonPage />;

  const data = listQuery.data;

  function SortHeader({ col }: { col: ColumnDef }) {
    if (!col.sortKey) return <span>{col.label}</span>;
    const active = sort.by === col.sortKey;
    const icon = !active ? faSort : sort.dir === 'asc' ? faSortUp : faSortDown;
    return (
      <button
        type="button"
        onClick={() => toggleSort(col.sortKey!)}
        className="flex items-center gap-1.5 hover:text-foreground"
      >
        {col.label}
        <FontAwesomeIcon icon={icon} className="h-3 w-3" />
      </button>
    );
  }

  return (
    <div className="space-y-5">
      <Breadcrumb
        segments={[
          { label: 'Operations', to: ROUTES.DASHBOARD_OPERATIONS },
          { label: 'Work Queue' },
        ]}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Operations Work Queue</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Search, filter, review, and act on every operational job in one place.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SearchBar
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search job #, equipment, customer…"
          />
          <button
            type="button"
            onClick={() => listQuery.refetch()}
            className="flex h-10 items-center gap-2 rounded-lg border border-input bg-card px-3 text-sm text-foreground hover:bg-muted"
            title="Refresh"
          >
            <FontAwesomeIcon
              icon={faArrowsRotate}
              className={listQuery.isFetching ? 'animate-spin' : ''}
            />
          </button>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`flex h-10 items-center gap-2 rounded-lg border border-input px-3 text-sm hover:bg-muted ${
              filtersOpen ? 'bg-muted text-foreground' : 'bg-card text-foreground'
            }`}
            title="Filter"
          >
            <FontAwesomeIcon icon={faFilter} />
            Filter
          </button>
          <button
            type="button"
            disabled
            title="Export not yet implemented"
            className="flex h-10 items-center gap-2 rounded-lg border border-input bg-card px-3 text-sm text-muted-foreground opacity-50 cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faFileExport} />
            Export
          </button>
        </div>
      </div>

      {/* TODO(OPS-28): Export is blocked on Stage 14/15 reporting/analytics infrastructure — see docs/OPS_IMPLEMENTATION_PLAN.md */}

      {statsQuery.isError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load statistics. Check your connection and refresh.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {statsQuery.isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <KpiCardSkeleton key={i} />)
        ) : statsQuery.data ? (
          <>
            <KpiCard
              label="Pending OPS Review"
              value={statsQuery.data.kpis.pendingOpsReviews}
              icon={faClipboardList}
              iconCls="bg-orange-500/10 text-orange-700"
              to={`${ROUTES.INSPECTIONS}?status=COMPLETED&operationsReviewStatus=PENDING`}
            />
            <KpiCard
              label="Returned to Engineer"
              value={statsQuery.data.kpis.returnedToEngineer}
              icon={faRotateLeft}
              iconCls="bg-red-500/10 text-red-700"
              to={`${ROUTES.INSPECTIONS}?operationsReviewStatus=RETURNED`}
            />
            <KpiCard
              label="Waiting for Commercial"
              value={statsQuery.data.kpis.waitingForCommercial}
              icon={faClockRotateLeft}
              iconCls="bg-green-500/10 text-green-700"
              to={`${ROUTES.INSPECTIONS}?operationsReviewStatus=APPROVED_FOR_COMMERCIAL`}
            />
            <KpiCard
              label="Inspection In Progress"
              value={statsQuery.data.kpis.inspectionInProgress}
              icon={faSpinner}
              iconCls="bg-amber-500/10 text-amber-700"
              to={`${ROUTES.INSPECTIONS}?status=IN_PROGRESS`}
            />
            <KpiCard
              label="Waiting Customer Decision"
              value={statsQuery.data.kpis.pendingCustomerDecisions}
              icon={faClockRotateLeft}
              iconCls="bg-purple-500/10 text-purple-700"
              to={`${ROUTES.INSPECTIONS}?customerApprovalStatus=PENDING`}
            />
          </>
        ) : null}
      </div>

      {filtersOpen && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Job Number
              </label>
              <input
                value={filters.jobNumber}
                onChange={(e) => setFilter('jobNumber', e.target.value)}
                placeholder="e.g. INS-2026-0001"
                className={`${SELECT_CLASS} w-full`}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Customer
              </label>
              <select
                value={filters.customerId}
                onChange={(e) => setFilter('customerId', e.target.value)}
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">All Customers</option>
                {(customersQuery.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {formatCustomerLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Equipment
              </label>
              <EquipmentSearchCombobox
                value={filters.equipmentId}
                initialLabel={filters.equipmentLabel}
                onSelect={(result: EquipmentSearchResult | null) => {
                  setFilters((prev) => ({
                    ...prev,
                    equipmentId: result?.id ?? '',
                    equipmentLabel: result
                      ? `${result.equipmentName} (${result.serialNumber})`
                      : '',
                  }));
                  setPage(1);
                }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Engineer
              </label>
              <select
                value={filters.engineerId}
                onChange={(e) => setFilter('engineerId', e.target.value)}
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">All Engineers</option>
                {(engineersQuery.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Priority
              </label>
              <select
                value={filters.priorityId}
                onChange={(e) => setFilter('priorityId', e.target.value)}
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">All Priorities</option>
                {(prioritiesQuery.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilter('status', e.target.value as InspectionStatus | '')}
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">All Statuses</option>
                {(Object.keys(INSPECTION_STATUS_LABELS) as InspectionStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {INSPECTION_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                OPS Review Status
              </label>
              <select
                value={filters.operationsReviewStatus}
                onChange={(e) =>
                  setFilter('operationsReviewStatus', e.target.value as OperationsReviewStatus | '')
                }
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">All Review Statuses</option>
                {(Object.keys(OPS_REVIEW_STATUS_LABELS) as OperationsReviewStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {OPS_REVIEW_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Customer Decision
              </label>
              <select
                value={filters.customerApprovalStatus}
                onChange={(e) =>
                  setFilter('customerApprovalStatus', e.target.value as CustomerApprovalStatus | '')
                }
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">All Customer Decisions</option>
                {(Object.keys(CUSTOMER_APPROVAL_LABELS) as CustomerApprovalStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {CUSTOMER_APPROVAL_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="wq-modification-round-state"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Modification Request
              </label>
              <select
                id="wq-modification-round-state"
                value={filters.modificationRoundState}
                onChange={(e) =>
                  setFilter('modificationRoundState', e.target.value as ModificationRoundState | '')
                }
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">Any / none</option>
                {OPEN_ROUND_STATES.map((s) => (
                  <option key={s} value={s}>
                    {ROUND_STATE_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Department
              </label>
              <select
                value={filters.departmentId}
                onChange={(e) => setFilter('departmentId', e.target.value)}
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">All Departments</option>
                {(departmentsQuery.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Received From
                </label>
                <input
                  type="date"
                  value={filters.receivedFrom}
                  onChange={(e) => setFilter('receivedFrom', e.target.value)}
                  className={`${DATE_CLASS} w-full`}
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Received To
                </label>
                <input
                  type="date"
                  value={filters.receivedTo}
                  onChange={(e) => setFilter('receivedTo', e.target.value)}
                  className={`${DATE_CLASS} w-full`}
                />
              </div>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-muted-foreground underline hover:text-foreground"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
            >
              {chip.label}
              <button onClick={chip.onClear} aria-label={`Clear ${chip.key} filter`}>
                <FontAwesomeIcon icon={faXmark} className="h-3 w-3 hover:text-foreground" />
              </button>
            </span>
          ))}
        </div>
      )}

      {listQuery.isError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load jobs. Check your connection and refresh.
        </div>
      )}

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-10 bg-muted/90 backdrop-blur">
                <tr className="border-b border-border text-left text-foreground">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`group relative px-4 py-3 font-medium ${col.hideBelow ? HIDE_CLASS[col.hideBelow] : ''}`}
                      style={{ width: widths[col.key] }}
                    >
                      <SortHeader col={col} />
                      <span
                        onMouseDown={startResize(col.key)}
                        className="absolute right-0 top-0 flex h-full w-2.5 cursor-col-resize select-none items-center justify-center"
                      >
                        <FontAwesomeIcon
                          icon={faGripLinesVertical}
                          className="pointer-events-none h-3 w-3 text-muted-foreground/0 group-hover:text-muted-foreground/60"
                        />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.data.map((item) => {
                  const isApproving = approvingId === item.id && approveMutation.isPending;
                  const canReview =
                    item.status === 'COMPLETED' && item.operationsReviewStatus === 'PENDING';
                  return (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td
                        className="px-4 py-3 font-medium text-primary"
                        style={{ width: widths.jobNumber }}
                      >
                        {item.inspectionNumber}
                        {/* Request Thread Stage B (OD-B3) — without this a
                            filtered row still reads as an ordinary job. Shown
                            only when the job actually has an open request, so
                            every other row is unchanged (B11). */}
                        {item.openModificationRound && (
                          <RequestThreadRowSummary round={item.openModificationRound} />
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 text-muted-foreground ${HIDE_CLASS.lg}`}
                        style={{ width: widths.receivedDate }}
                      >
                        {fmtDate(item.intakeReceivedAt)}
                      </td>
                      <td
                        className="px-4 py-3 text-foreground truncate"
                        style={{ width: widths.customer }}
                      >
                        {item.customerName ?? '—'}
                      </td>
                      <td
                        className={`px-4 py-3 text-muted-foreground truncate ${HIDE_CLASS.sm}`}
                        style={{ width: widths.equipment }}
                      >
                        {item.equipmentName}
                      </td>
                      <td
                        className={`px-4 py-3 font-mono text-muted-foreground ${HIDE_CLASS.lg}`}
                        style={{ width: widths.serialNumber }}
                      >
                        {item.equipmentSerialNumber}
                      </td>
                      <td
                        className={`px-4 py-3 text-muted-foreground truncate ${HIDE_CLASS.md}`}
                        style={{ width: widths.engineer }}
                      >
                        {item.assignedEngineerName ?? '—'}
                      </td>
                      <td
                        className={`px-4 py-3 ${HIDE_CLASS.md}`}
                        style={{ width: widths.priority }}
                      >
                        {item.priorityName ?? '—'}
                      </td>
                      <td className="px-4 py-3" style={{ width: widths.currentStage }}>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STAGE_BADGE[item.currentStage]}`}
                        >
                          {WORK_QUEUE_STAGE_LABELS[item.currentStage]}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3 text-muted-foreground ${HIDE_CLASS.lg}`}
                        style={{ width: widths.lastUpdated }}
                      >
                        {fmtDate(item.updatedAt)}
                      </td>
                      <td
                        className={`px-4 py-3 text-muted-foreground ${HIDE_CLASS.lg}`}
                        style={{ width: widths.daysWaiting }}
                      >
                        {item.daysWaiting}d
                      </td>
                      <td className="px-4 py-3" style={{ width: widths.actions }}>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(openTarget(item.id))}
                            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label="Open"
                            title="Open"
                          >
                            <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                          </button>
                          {canReview && can('inspection:operations-review') && (
                            <>
                              <button
                                onClick={() => void handleApprove(item)}
                                disabled={isApproving}
                                className="flex h-7 w-7 items-center justify-center rounded-lg text-green-700 transition-colors hover:bg-green-500/10 disabled:opacity-50"
                                aria-label="Approve"
                                title="Approve"
                              >
                                <FontAwesomeIcon
                                  icon={isApproving ? faSpinner : faCheck}
                                  className={`h-3.5 w-3.5 ${isApproving ? 'animate-spin' : ''}`}
                                />
                              </button>
                              <button
                                onClick={() => setReturnTarget(item)}
                                className="flex h-7 w-7 items-center justify-center rounded-lg text-red-700 transition-colors hover:bg-red-500/10"
                                aria-label="Return"
                                title="Return"
                              >
                                <FontAwesomeIcon icon={faRotateLeft} className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() =>
                              navigate(ROUTES.INSPECTION_DETAIL.replace(':id', item.id))
                            }
                            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label="View Timeline"
                            title="View Timeline"
                          >
                            <FontAwesomeIcon icon={faClockRotateLeft} className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : data ? (
        <EmptyState
          icon={faClipboardList}
          title={
            debouncedSearch || activeChips.length > 0
              ? 'No jobs match your filters'
              : 'No operational jobs yet'
          }
          description={
            debouncedSearch || activeChips.length > 0
              ? 'Try adjusting your search or filters.'
              : 'Jobs appear here once equipment inward records and inspections are created.'
          }
        />
      ) : null}

      <ReturnToEngineerDialog
        target={returnTarget}
        onClose={() => setReturnTarget(null)}
        onReturned={() => {
          setReturnTarget(null);
          invalidateAll();
        }}
      />
    </div>
  );
}

/**
 * Request Thread Stage B — the request context a discovery row needs.
 *
 * Request identity, who the workflow is waiting on, and the customer's current
 * ask. Deliberately NOT the assignee: a modification round's `assignedUserId` is
 * a soft claim, and showing it beside "waiting on" would invite reading it as
 * ownership — the confusion that produced the INS-00201 defect.
 *
 * "you" is used only when Operations is the awaited party; this row lives on the
 * Operations queue, which is `inspection:operations-review`-gated.
 */
function RequestThreadRowSummary({ round }: { round: OpenModificationRoundSummary }) {
  const awaitingOperations = round.waitingFor === 'OPERATIONS';
  const ask = round.latestCustomerRequestText?.trim();
  return (
    <div className="mt-1 space-y-0.5 font-normal">
      <div className="flex flex-wrap items-center gap-1.5">
        {/* STAGE C-vi — the request's own workspace. The job number above stays a
            job link: a row is a JOB that happens to carry a request, and the two
            destinations answer different questions. Keyed by the round id the
            Stage B summary already carries, so this adds no API call. */}
        <Link
          to={ROUTES.REQUEST_THREAD.replace(':roundId', round.roundId)}
          onClick={(e) => e.stopPropagation()}
          className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted/70 hover:text-foreground hover:underline"
        >
          Request #{round.roundNumber}
        </Link>
        <span
          className={`text-[11px] font-medium ${
            awaitingOperations ? 'text-destructive' : 'text-muted-foreground'
          }`}
        >
          {awaitingOperations
            ? 'Waiting on you'
            : `Waiting on ${round.waitingFor ? round.waitingFor.toLowerCase() : 'the service team'}`}
        </span>
      </div>
      {ask && (
        <p className="line-clamp-2 text-[11px] text-muted-foreground" title={ask}>
          “{ask}”
        </p>
      )}
    </div>
  );
}
